// Two-way filtering between the Feature Name and Customer Type Name dropdowns.
//
// - Picking a feature narrows the type list to that feature's types.
// - Picking a type narrows the feature list to features that have that type.
// - Picking "All" on either side clears the other side and widens both lists.
// - A specific pick keeps the other side's value if it is still valid, else clears it.

export interface FeatureTypePair {
  featureName: string;
  customerTypeName: string;
}

export interface SelectOption {
  label?: string;
  value?: string;
}

export const ALL_OPTION: SelectOption = { label: "All", value: "" };

export interface FilterSelection {
  feature: SelectOption | null;
  type: SelectOption | null;
}

function isSpecific(option: SelectOption | null): option is SelectOption & { value: string } {
  return !!option?.value;
}

function toOptions(values: Iterable<string>): SelectOption[] {
  return [...new Set(values)].sort().map(v => ({ label: v, value: v }));
}

export function featureOptions(configs: FeatureTypePair[], selectedType: SelectOption | null): SelectOption[] {
  const matching = isSpecific(selectedType)
    ? configs.filter(c => c.customerTypeName === selectedType.value)
    : configs;
  return [ALL_OPTION, ...toOptions(matching.map(c => c.featureName))];
}

export function typeOptions(configs: FeatureTypePair[], selectedFeature: SelectOption | null): SelectOption[] {
  const matching = isSpecific(selectedFeature)
    ? configs.filter(c => c.featureName === selectedFeature.value)
    : configs;
  return [ALL_OPTION, ...toOptions(matching.map(c => c.customerTypeName))];
}

export function selectFeature(
  configs: FeatureTypePair[], next: SelectOption | null, currentType: SelectOption | null,
): FilterSelection {
  if (!isSpecific(next)) return { feature: next, type: null };
  const typeStillValid = isSpecific(currentType)
    && configs.some(c => c.featureName === next.value && c.customerTypeName === currentType.value);
  return { feature: next, type: typeStillValid ? currentType : null };
}

export function selectType(
  configs: FeatureTypePair[], next: SelectOption | null, currentFeature: SelectOption | null,
): FilterSelection {
  if (!isSpecific(next)) return { feature: null, type: next };
  const featureStillValid = isSpecific(currentFeature)
    && configs.some(c => c.customerTypeName === next.value && c.featureName === currentFeature.value);
  return { feature: featureStillValid ? currentFeature : null, type: next };
}

export function matchesSelection(item: FeatureTypePair, selection: FilterSelection): boolean {
  return (!isSpecific(selection.feature) || item.featureName === selection.feature.value)
    && (!isSpecific(selection.type) || item.customerTypeName === selection.type.value);
}
